import _ from 'lodash';
import React, { ReactElement, useCallback, useState } from 'react';
import { Button, CheckboxOptionType, Input, InputNumber, Radio, RadioChangeEvent, Select, Space } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import IDealProperty, {
    IDealPropertyErrors,
} from '@/Api/Deal/IDealProperty';
import PropertyStateEnum from '@/Api/Deal/PropertyStateEnum';
import PropertyStreetTypeEnum from '@/Api/Deal/PropertyStreetTypeEnum';
import ZoneTypeEnum from '@/Api/Deal/ZoneTypeEnum';
import {
    dealAddressSearchAction,
    dealAddressSearchResultsClearAction,
    dealPropertyGnafAddressDetailAction,
    propertyDeleteAction,
    propertyErrorSetAction,
    propertyValueSetAction,
} from '@/Lead/actions';
import {
    dealPropertyAddressSearchResultsLoadingSelector,
    dealPropertyAddressSearchResultsSelector,
    dealPropertyGnafAddressDetailLoadingSelector,
    propertySelector,
} from '@/Lead/selectors';
import {
    validateCurrentDebt,
    validateEstimatedValue,
    validateGnafAddressDetailPid,
    validatePostcode,
    validateState,
    validateStreetName,
    validateStreetNumber,
    validateStreetType,
    validateSuburb,
    validateUnitNumber,
    validateZoneType,
} from '@/Lead/validators';
import propertyStreetTypeLabels from '@/Api/Deal/propertyStreetTypeLabels';
import { IGlobalState } from '@/reducer';
import MoneySign from '@/UI/MoneySign';
import Down from '@/UI/Down';
import { Dispatch } from 'redux';
import { DefaultOptionType } from 'antd/es/select';
import FormItem from '@/UI/FormItem';
import SearchSelect, {
    ISearchOption,
} from '@/UI/SearchSelect';
import IAddressSearchResult from '@/Api/AddressSearch/IAddressSearchResult';
import AddressVersionEnum from '@/Api/Deal/AddressVersionEnum';
import Residential from '@/UI/Residential';
import Commercial from '@/UI/Commercial';
import RuralResidential from '@/UI/RuralResidential';
import { IDictionary } from '@/utilities/IDictionary';
import PropertyCategoryEnum from '@/Borrow/PropertyCategoryEnum';
import { propertyCategoryLabels } from '@/Borrow/propertyCategoryLabels';

const zoneTypeLabels: IDictionary<string> = {
    [ZoneTypeEnum.CommercialIndustrial]: 'Industrial',
    [ZoneTypeEnum.CommercialLand]: 'Land',
    [ZoneTypeEnum.CommercialOffice]: 'Office',
    [ZoneTypeEnum.CommercialRetail]: 'Retail',
    [ZoneTypeEnum.ResidentialHouse]: 'House',
    [ZoneTypeEnum.ResidentialLand]: 'Land',
    [ZoneTypeEnum.ResidentialTownhouse]: 'Townhouse',
    [ZoneTypeEnum.ResidentialUnit]: 'Unit',
    [ZoneTypeEnum.RuralLand]: 'Land',
    [ZoneTypeEnum.RuralResidential]: 'Residential',
};

const categoriesToZoneTypes: IDictionary<ZoneTypeEnum[]> = {
    [PropertyCategoryEnum.Residential]: [
        ZoneTypeEnum.ResidentialHouse,
        ZoneTypeEnum.ResidentialTownhouse,
        ZoneTypeEnum.ResidentialUnit,
        ZoneTypeEnum.ResidentialLand,
    ],
    [PropertyCategoryEnum.Commercial]: [
        ZoneTypeEnum.CommercialOffice,
        ZoneTypeEnum.CommercialRetail,
        ZoneTypeEnum.CommercialIndustrial,
        ZoneTypeEnum.CommercialLand,
    ],
    [PropertyCategoryEnum.RuralResidential]: [
        ZoneTypeEnum.RuralResidential,
        ZoneTypeEnum.RuralLand,
    ],
};

const propertyCategoryIcons: IDictionary<JSX.Element> = {
    [PropertyCategoryEnum.Residential]: <Residential className='icon' />,
    [PropertyCategoryEnum.Commercial]: <Commercial className='icon' />,
    [PropertyCategoryEnum.RuralResidential]: <RuralResidential className='icon' />,
};

export default function Property(props: {
    index: number;
    uuid: string;
}): ReactElement {
    const { index, uuid } = props;
    const dispatch: Dispatch = useDispatch();
    const property: IDealProperty = useSelector((state: IGlobalState) => propertySelector(state, uuid));
    const [ propertyCategory, setPropertyCategory ] = useState<PropertyCategoryEnum>(() => {
        _.each(categoriesToZoneTypes, (zoneTypes: ZoneTypeEnum[], zoneCategory: string) => {
            _.each(zoneTypes, (zoneType: ZoneTypeEnum) => {
                if (property.zoneType === zoneType) {
                    return zoneCategory as PropertyCategoryEnum;
                }
            });
        });
    });
    const addressSearchResults: IAddressSearchResult[] = useSelector(dealPropertyAddressSearchResultsSelector);
    const addressSearchResultsLoading: boolean = useSelector(dealPropertyAddressSearchResultsLoadingSelector);
    const gnafAddressDetailLoading: boolean = useSelector(dealPropertyGnafAddressDetailLoadingSelector);

    const setError: (key: keyof IDealPropertyErrors, value: string) => void = useCallback((key: keyof IDealPropertyErrors, value: string) => {
        dispatch(propertyErrorSetAction(uuid, key, value));
    }, [
        dispatch,
        uuid,
    ]);

    const remove: () => void = useCallback((): void => {
        dispatch(propertyDeleteAction(uuid));
    }, [
        dispatch,
        uuid,
    ]);

    const onChangeUnitNumber: (event: React.ChangeEvent<HTMLInputElement>) => void = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
        dispatch(propertyValueSetAction(uuid, 'unitNumber', event.target.value));
    }, [
        dispatch,
        uuid,
    ]);

    const onChangeStreetNumber: (event: React.ChangeEvent<HTMLInputElement>) => void = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
        dispatch(propertyValueSetAction(uuid, 'streetNumber', event.target.value));
    }, [
        dispatch,
        uuid,
    ]);

    const onChangeStreetName: (event: React.ChangeEvent<HTMLInputElement>) => void = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
        dispatch(propertyValueSetAction(uuid, 'streetName', event.target.value));
    }, [
        dispatch,
        uuid,
    ]);

    const onChangeStreetType: (value: PropertyStreetTypeEnum) => void = useCallback((value: PropertyStreetTypeEnum): void => {
        dispatch(propertyValueSetAction(uuid, 'streetType', value));
    }, [
        dispatch,
        uuid,
    ]);

    const onChangePostcode: (event: React.ChangeEvent<HTMLInputElement>) => void = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
        if (/[^0-9]/.test(event.target.value) || event.target.value.trim().length > 4) {
            return;
        }
        dispatch(propertyValueSetAction(uuid, 'postcode', event.target.value));
    }, [
        dispatch,
        uuid,
    ]);

    const onChangePropertyCategory: (e: RadioChangeEvent) => void = useCallback((e: RadioChangeEvent) => {
        if (e.target.value !== propertyCategory && property.zoneType) {
            dispatch(propertyValueSetAction(uuid, 'zoneType', null));
        }
        setPropertyCategory(e.target.value);
    }, [
        dispatch,
        property.zoneType,
        propertyCategory,
        uuid,
    ]);

    const onChangeZoneType: (e: RadioChangeEvent) => void = useCallback((e: RadioChangeEvent): void => {
        dispatch(propertyValueSetAction(uuid, 'zoneType', e.target.value || null));
    }, [
        dispatch,
        uuid,
    ]);

    const onChangeEstimatedValue: (value: number) => void = useCallback((value: number): void => {
        if (value < 0 || value > 99999999) {
            return;
        }

        dispatch(propertyValueSetAction(uuid, 'estimatedValue', value && value.toString() || null));
    }, [
        dispatch,
        uuid,
    ]);

    const onChangeCurrentDebt: (value: number) => void = useCallback((value: number): void => {
        if (value < 0 || value > 99999999) {
            return;
        }

        dispatch(propertyValueSetAction(uuid, 'currentDebt', value && value.toString() || null));
    }, [
        dispatch,
        uuid,
    ]);

    const onChangeState: (value: PropertyStateEnum) => void = useCallback((value: PropertyStateEnum): void => {
        dispatch(propertyValueSetAction(uuid, 'state', value || null));
    }, [
        dispatch,
        uuid,
    ]);

    const onChangeSuburb: (event: React.ChangeEvent<HTMLInputElement>) => void = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
        dispatch(propertyValueSetAction(uuid, 'suburb', event.target.value.substr(0, 50)));
    }, [
        dispatch,
        uuid,
    ]);

    const onBlurUnitNumber: () => boolean = useCallback((): boolean => {
        return !validateUnitNumber(property.unitNumber, setError);
    }, [
        property.unitNumber,
        setError,
    ]);

    const onBlurStreetNumber: () => boolean = useCallback((): boolean => {
        return !validateStreetNumber(property.streetNumber, setError);
    }, [
        property.streetNumber,
        setError,
    ]);

    const onBlurStreetName: () => boolean = useCallback((): boolean => {
        return !validateStreetName(property.streetName, setError);
    }, [
        property.streetName,
        setError,
    ]);

    const onBlurStreetType: () => boolean = useCallback((): boolean => {
        return !validateStreetType(property.streetType, setError);
    }, [
        property.streetType,
        setError,
    ]);

    const onBlurPostcode: () => boolean = useCallback((): boolean => {
        return !validatePostcode(property.postcode, property.state, setError);
    }, [
        property.postcode,
        property.state,
        setError,
    ]);

    const onBlurZoneType: () => boolean = useCallback((): boolean => {
        return !validateZoneType(property.zoneType, setError);
    }, [
        property.zoneType,
        setError,
    ]);

    const onBlurEstimatedValue: () => boolean = useCallback((): boolean => {
        return !validateEstimatedValue(property.estimatedValue, setError);
    }, [
        property.estimatedValue,
        setError,
    ]);

    const onBlurCurrentDebt: () => boolean = useCallback((): boolean => {
        return !validateCurrentDebt(property.currentDebt, setError);
    }, [
        property.currentDebt,
        setError,
    ]);

    const onBlurGnafAddressDetailPid: () => boolean = useCallback((): boolean => {
        return !validateGnafAddressDetailPid(property.gnafAddressDetailPid, setError);
    }, [
        property.gnafAddressDetailPid,
        setError,
    ]);

    const onBlurState: () => boolean = useCallback((): boolean => {
        if (property.postcode && property.postcode.length > 0) {
            validatePostcode(property.postcode, property.state, setError);
        }

        return !validateState(property.state, setError);
    }, [
        property.postcode,
        property.state,
        setError,
    ]);

    const onBlurSuburb: () => boolean = useCallback((): boolean => {
        return !validateSuburb(property.suburb, setError);
    }, [
        property.suburb,
        setError,
    ]);

    const onSearchAddress: (keyword: string) => void = useCallback((keyword: string): void => {
        dispatch(dealAddressSearchAction(keyword));
    }, [
        dispatch,
    ]);

    const onSearchClearAddress: () => void = useCallback((): void => {
        dispatch(dealAddressSearchResultsClearAction());
    }, [
        dispatch,
    ]);

    const onSelectAddress: (gnafAddressDetailPid: string) => void = useCallback((gnafAddressDetailPid: string) => {
        dispatch(dealPropertyGnafAddressDetailAction(property.uuid, gnafAddressDetailPid));
    }, [
        dispatch,
        property.uuid,
    ]);

    const onClickManualEntry: () => void = useCallback(() => {
        dispatch(propertyValueSetAction(uuid, 'unitNumber', null));
        dispatch(propertyValueSetAction(uuid, 'streetNumber', null));
        dispatch(propertyValueSetAction(uuid, 'streetType', null));
        dispatch(propertyValueSetAction(uuid, 'streetName', null));
        dispatch(propertyValueSetAction(uuid, 'suburb', null));
        dispatch(propertyValueSetAction(uuid, 'state', null));
        dispatch(propertyValueSetAction(uuid, 'postcode', null));
        dispatch(propertyValueSetAction(uuid, 'gnafAddressDetailPid', null));
        dispatch(propertyValueSetAction(uuid, 'addressVersion', AddressVersionEnum.V2));
    }, [
        dispatch,
        uuid,
    ]);

    const onClickUseSearch: () => void = useCallback(() => {
        dispatch(propertyValueSetAction(uuid, 'unitNumber', null));
        dispatch(propertyValueSetAction(uuid, 'streetNumber', null));
        dispatch(propertyValueSetAction(uuid, 'streetType', null));
        dispatch(propertyValueSetAction(uuid, 'streetName', null));
        dispatch(propertyValueSetAction(uuid, 'suburb', null));
        dispatch(propertyValueSetAction(uuid, 'state', null));
        dispatch(propertyValueSetAction(uuid, 'postcode', null));
        dispatch(propertyValueSetAction(uuid, 'gnafAddressDetailPid', null));
        dispatch(propertyValueSetAction(uuid, 'addressVersion', AddressVersionEnum.GNAF_V1));
    }, [
        dispatch,
        uuid,
    ]);

    const streetTypeOptions: DefaultOptionType[] = _.keys(propertyStreetTypeLabels).map((code: string) => ({
        label: propertyStreetTypeLabels[code],
        value: code,
    }));

    const stateOptions: DefaultOptionType[] = (property.state === null ? [ { value: '', label: '' } ] : []).concat([
        { label: 'ACT', value: PropertyStateEnum.AustralianCapitalTerritory },
        { label: 'NSW', value: PropertyStateEnum.NewSouthWales },
        { label: 'NT', value: PropertyStateEnum.NorthernTerritory },
        { label: 'QLD', value: PropertyStateEnum.Queensland },
        { label: 'SA', value: PropertyStateEnum.SouthAustralia },
        { label: 'TAS', value: PropertyStateEnum.Tasmania },
        { label: 'VIC', value: PropertyStateEnum.Victoria },
        { label: 'WA', value: PropertyStateEnum.WesternAustralia },
    ]);

    const propertyCategoryOptions: CheckboxOptionType<string>[] = _.map(propertyCategoryIcons, (element: JSX.Element, propertyType: string): CheckboxOptionType<string> => ({
        label: (
            <Space direction='vertical'>
                {propertyCategoryIcons[propertyType]}
                <span>{propertyCategoryLabels[propertyType]}</span>
            </Space>
        ),
        value: propertyType,
    }));

    const zoneTypeOptions: CheckboxOptionType<string>[] = _.map(categoriesToZoneTypes[propertyCategory], (zoneType: string): CheckboxOptionType<string> => ({
        label: (
            <span>{zoneTypeLabels[zoneType]}</span>
        ),
        value: zoneType,
    }));

    const searchOptions: ISearchOption[] = addressSearchResults && _.map(addressSearchResults, (addressSearchResult: IAddressSearchResult) => ({
        label: addressSearchResult.longFormat,
        value: addressSearchResult.gnafAddressDetailPid,
    }));

    const propertyTypeBlock: React.ReactElement = propertyCategory && (
        <FormItem className='property-type'
            help={property.errors.zoneType}
            label='Property Type'
            layout='vertical'
            validateStatus={property.errors.zoneType ? 'error' : ''}
        >
            <Radio.Group
                buttonStyle='solid'
                className='card-radio-group'
                options={zoneTypeOptions}
                value={property.zoneType}
                onBlur={onBlurZoneType}
                onChange={onChangeZoneType}
            />
        </FormItem>
    );

    const streetAddressFormBlock: React.ReactElement = property.addressVersion === AddressVersionEnum.GNAF_V1 ? (
        <>
            <FormItem
                className='address-search'
                help={property.errors.gnafAddressDetailPid}
                label='Current Residential Address'
                layout='vertical'
                validateStatus={property.errors.gnafAddressDetailPid && 'error'}
            >
                <SearchSelect
                    defaultText={property.gnafAddressDetailPid && property.formattedAddress}
                    loading={addressSearchResultsLoading || gnafAddressDetailLoading}
                    options={searchOptions}
                    value={property.gnafAddressDetailPid}
                    onBlur={onBlurGnafAddressDetailPid}
                    onSearch={onSearchAddress}
                    onSearchClear={onSearchClearAddress}
                    onSelect={onSelectAddress}
                />
            </FormItem>
            <Button className='use-manual' type='link' onClick={onClickManualEntry}>Enter manually</Button>
        </>
    ) : (
        <>
            <FormItem
                className='unit-number'
                help={property.errors.unitNumber}
                label='Unit number (if any)'
                layout='vertical'
                name='unitNumber'
                validateStatus={property.errors.unitNumber ? 'error' : ''}
            >
                <Input
                    maxLength={10}
                    placeholder='Enter unit number'
                    value={property.unitNumber}
                    onBlur={onBlurUnitNumber}
                    onChange={onChangeUnitNumber}
                />
            </FormItem>
            <FormItem
                className='street-number'
                help={property.errors.streetNumber}
                label='Street number'
                layout='vertical'
                name='streetNumber'
                validateStatus={property.errors.streetNumber ? 'error' : ''}
            >
                <Input
                    maxLength={10}
                    placeholder='Enter street number'
                    value={property.streetNumber}
                    onBlur={onBlurStreetNumber}
                    onChange={onChangeStreetNumber}
                />
            </FormItem>
            <FormItem
                className='street-name'
                help={property.errors.streetName}
                label='Street name'
                layout='vertical'
                name='streetName'
                validateStatus={property.errors.streetName ? 'error' : ''}
            >
                <Input
                    maxLength={255}
                    placeholder='Enter street name'
                    value={property.streetName}
                    onBlur={onBlurStreetName}
                    onChange={onChangeStreetName}
                />
            </FormItem>
            <FormItem
                className='street-type'
                help={property.errors.streetType}
                label='Street type'
                layout='vertical'
                name='streetType'
                validateStatus={property.errors.streetType ? 'error' : ''}
            >
                <Select
                    optionFilterProp='label'
                    options={streetTypeOptions}
                    placeholder='Select a street type'
                    showSearch={true}
                    suffixIcon={<Down />}
                    value={property.streetType}
                    onBlur={onBlurStreetType}
                    onChange={onChangeStreetType}
                />
            </FormItem>
            <FormItem
                className='suburb'
                help={property.errors.suburb}
                label='Suburb'
                layout='vertical'
                name='suburb'
                validateStatus={property.errors.suburb ? 'error' : ''}
            >
                <Input
                    maxLength={50}
                    placeholder='Enter suburb name'
                    value={property.suburb || ''}
                    onBlur={onBlurSuburb}
                    onChange={onChangeSuburb}
                />
            </FormItem>
            <FormItem
                className='state'
                help={property.errors.state}
                label='State'
                layout='vertical'
                name='state'
                validateStatus={property.errors.state ? 'error' : ''}
            >
                <Select
                    options={stateOptions}
                    placeholder='Select a state'
                    showSearch={true}
                    suffixIcon={<Down />}
                    value={property.state}
                    onBlur={onBlurState}
                    onChange={onChangeState}
                />
            </FormItem>
            <FormItem
                className='postcode'
                help={property.errors.postcode}
                label='Postcode'
                layout='vertical'
                name='postcode'
                validateStatus={property.errors.postcode ? 'error' : ''}
            >
                <Input
                    placeholder='Enter a postcode'
                    value={property.postcode || ''}
                    onBlur={onBlurPostcode}
                    onChange={onChangePostcode}
                />
            </FormItem>
        </>
    );

    return (
        <div className='property'>
            {index !== 0 && (<h4 className='property-header'>Property {index + 1}</h4>)}
            {property.addressVersion !== AddressVersionEnum.GNAF_V1 && (<Button className='use-search' type='link' onClick={onClickUseSearch}>Use address search</Button>)}
            <div className='property-form'>
                {streetAddressFormBlock}
                <FormItem
                    className='property-category'
                    help={(property.errors.zoneType && !propertyCategory) && 'Please select a category'}
                    label='Property Category'
                    layout='vertical'
                    name='propertyType'
                    validateStatus={(property.errors.zoneType && !propertyCategory) ? 'error' : ''}
                >
                    <Radio.Group
                        buttonStyle='solid'
                        className='card-radio-group'
                        options={propertyCategoryOptions}
                        value={propertyCategory}
                        onBlur={!propertyCategory ? onBlurZoneType : null}
                        onChange={onChangePropertyCategory}
                    />
                </FormItem>
                {propertyTypeBlock}
                <FormItem
                    className='estimated-value'
                    help={property.errors.estimatedValue}
                    label='Estimated current value'
                    layout='vertical'
                    name='estimatedValue'
                    validateStatus={property.errors.estimatedValue ? 'error' : ''}
                >
                    <InputNumber
                        addonBefore={<MoneySign />}
                        className='dollar-value'
                        controls={false}
                        min={0}
                        placeholder='e.g. “500,000”'
                        value={property.estimatedValue}
                        onBlur={onBlurEstimatedValue}
                        onChange={onChangeEstimatedValue}
                    />
                </FormItem>
                <FormItem
                    className='current-debt'
                    help={property.errors.currentDebt}
                    label='Remaining mortgage balance'
                    layout='vertical'
                    name='currentDebt'
                    validateStatus={property.errors.currentDebt ? 'error' : ''}
                >
                    <InputNumber
                        addonBefore={<MoneySign />}
                        className='dollar-value'
                        controls={false}
                        min={0}
                        placeholder='e.g. “500,000”'
                        value={property.currentDebt}
                        onBlur={onBlurCurrentDebt}
                        onChange={onChangeCurrentDebt}
                    />
                </FormItem>
            </div>
            {index > 0 && (<Button className='remove-property' onClick={remove}>Delete property</Button>)}
        </div>
    );
}